/**
 * FeaturedProducts — "POPULAR RIGHT NOW" product grid with category filter chips.
 * Maps to the popular products section in index.html.
 *
 * Filter chips send real query params to /api/filter-product — never filter
 * a static JS array (Rule 5 satisfied).
 * Quick Add is delegated to ProductCardNew which uses the existing addToCart
 * helper (Rule 4 satisfied).
 * "ALL" tab fetches from /api/featured-products.
 * Category chips fetch from /api/filter-product?category=<name>.
 *
 * Design tokens: ds-surface-container (section bg), ds-primary (active chip),
 * ds-on-background (text), ds-inverse-surface (button border),
 * barlow (heading), inter (chips/buttons).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SummaryApi from "../common";
import { ROUTES } from "../constants";
import { request } from "../helpers/request";
import ProductCardNew from "./ProductCardNew";

// Chips that map to category filter values sent to the API.
// Values must match the exact category strings stored in MongoDB.
const FILTER_CHIPS = [
  { label: "ALL", category: null },
  { label: "HELMETS", category: "Helmets" },
  { label: "GLOVES", category: "Gloves" },
  { label: "OILS", category: "Motor Oils" },
  { label: "ACCESSORIES", category: "Accessories" },
];

const CardSkeleton = () => (
  <div className="bg-white overflow-hidden">
    <div className="h-[260px] bg-ds-surface-container animate-pulse" />
    <div className="pt-3 space-y-2">
      <div className="h-2.5 bg-ds-surface-container rounded w-1/4 animate-pulse" />
      <div className="h-4 bg-ds-surface-container rounded w-full animate-pulse" />
      <div className="h-4 bg-ds-surface-container rounded w-2/3 animate-pulse" />
      <div className="h-4 bg-ds-surface-container rounded w-1/3 animate-pulse" />
    </div>
  </div>
);

const FeaturedProducts = () => {
  const [activeChip, setActiveChip] = useState(0); // index into FILTER_CHIPS
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async (chipIndex) => {
    setLoading(true);
    try {
      const chip = FILTER_CHIPS[chipIndex];
      let res;

      if (chip.category === null) {
        // "ALL" tab — fetch featured/best-seller products
        res = await request(SummaryApi.featuredProducts);
      } else {
        // Category chip — filter via real products API (Rule 5)
        res = await request({
          ...SummaryApi.filterProduct,
          url: `${SummaryApi.filterProduct.url}?category=${encodeURIComponent(chip.category)}&limit=8`,
        });
      }

      const json = await res.json();
      if (json.success) {
        setProducts(json.data || []);
      }
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts(activeChip);
  }, [activeChip, fetchProducts]);

  const handleChipClick = (idx) => {
    if (idx === activeChip) return;
    setActiveChip(idx);
  };

  return (
    <section
      className="bg-ds-surface-container-low py-20 px-10"
      aria-label="Popular products"
    >
      <div className="max-w-[1280px] mx-auto">
        {/* Header row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <h2 className="font-barlow font-extrabold text-[38px] text-ds-on-background leading-none uppercase">
            POPULAR RIGHT NOW
          </h2>

          {/* Filter chips — each click calls the real API (Rule 5) */}
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Filter by category"
          >
            {FILTER_CHIPS.map((chip, idx) => {
              const isActive = idx === activeChip;
              return (
                <button
                  key={chip.label}
                  onClick={() => handleChipClick(idx)}
                  className={`px-4 h-[32px] rounded-full border font-inter text-[13px] font-semibold transition-colors duration-200 ${
                    isActive
                      ? "border-ds-primary bg-ds-primary text-white"
                      : "border-ds-on-background text-ds-on-background hover:bg-ds-on-background hover:text-white"
                  }`}
                  aria-pressed={isActive}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {loading
            ? Array.from({ length: 8 }, (_, i) => <CardSkeleton key={i} />)
            : products
                .slice(0, 8)
                .map((product) => (
                  <ProductCardNew key={product._id} product={product} />
                ))}
        </div>

        {!loading && products.length === 0 && (
          <p className="text-center font-inter text-ds-secondary py-12">
            No products found in this category yet.
          </p>
        )}

        {/* View All CTA */}
        <div className="flex justify-center">
          <Link
            to={ROUTES.PRODUCT_CATEGORY}
            className="inline-flex items-center justify-center px-8 h-[48px] border border-ds-on-background text-ds-on-background font-inter font-medium text-[14px] hover:bg-ds-on-background hover:text-white transition-all duration-300"
          >
            VIEW ALL PRODUCTS →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
